import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [priceConfig, setPriceConfig] = useState({});
  const [businessRules, setBusinessRules] = useState({});
  const [companyInfo, setCompanyInfo] = useState({});
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [whatsappConfig, setWhatsappConfig] = useState({});
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [messageLogs, setMessageLogs] = useState([]);
  const [bankConfig, setBankConfig] = useState({});
  const [pixPayments, setPixPayments] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);

  useEffect(() => {
    fetchBookings();
    fetchUsers();
    fetchConfigurations();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const fetchConfigurations = async () => {
    try {
      const [pricesRes, rulesRes, companyRes, linksRes, whatsappRes, templatesRes, logsRes, bankRes, pixRes, webhooksRes] = await Promise.all([
        axios.get(`${API}/config/prices`),
        axios.get(`${API}/config/rules`),
        axios.get(`${API}/config/company`),
        axios.get(`${API}/payment-links`),
        axios.get(`${API}/config/whatsapp`),
        axios.get(`${API}/message-templates`),
        axios.get(`${API}/message-logs`),
        axios.get(`${API}/config/bank`),
        axios.get(`${API}/pix/payments`),
        axios.get(`${API}/webhook/events`)
      ]);
      
      setPriceConfig(pricesRes.data);
      setBusinessRules(rulesRes.data);
      setCompanyInfo(companyRes.data);
      setPaymentLinks(linksRes.data);
      setWhatsappConfig(whatsappRes.data);
      setMessageTemplates(templatesRes.data);
      setMessageLogs(logsRes.data);
      setBankConfig(bankRes.data);
      setPixPayments(pixRes.data);
      setWebhookEvents(webhooksRes.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const confirmPayment = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/confirm-payment`);
      alert('Pagamento confirmado com sucesso!');
      fetchBookings(); // Refresh data
    } catch (error) {
      console.error('Erro ao confirmar pagamento:', error);
      alert('Erro ao confirmar pagamento');
    }
  };

  const cancelBooking = async (bookingId) => {
    if (window.confirm('Tem certeza que deseja cancelar este agendamento?')) {
      try {
        await axios.delete(`${API}/bookings/${bookingId}`);
        alert('Agendamento cancelado com sucesso!');
        fetchBookings(); // Refresh data
      } catch (error) {
        console.error('Erro ao cancelar agendamento:', error);
        alert('Erro ao cancelar agendamento');
      }
    }
  };

  // Configuration functions
  const savePriceConfig = async () => {
    try {
      await axios.put(`${API}/config/prices`, priceConfig);
      alert('Configuração de preços salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar preços:', error);
      alert('Erro ao salvar configuração de preços');
    }
  };

  const saveBusinessRules = async () => {
    try {
      await axios.put(`${API}/config/rules`, businessRules);
      alert('Regras de negócio salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar regras:', error);
      alert('Erro ao salvar regras de negócio');
    }
  };

  const saveCompanyInfo = async () => {
    try {
      await axios.put(`${API}/config/company`, companyInfo);
      alert('Informações da empresa salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar informações:', error);
      alert('Erro ao salvar informações da empresa');
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja desativar este usuário?')) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        alert('Usuário desativado com sucesso!');
        fetchUsers(); // Refresh data
      } catch (error) {
        console.error('Erro ao desativar usuário:', error);
        alert('Erro ao desativar usuário');
      }
    }
  };

  const saveWhatsAppConfig = async () => {
    try {
      await axios.put(`${API}/config/whatsapp`, whatsappConfig);
      alert('Configuração do WhatsApp salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar WhatsApp:', error);
      alert('Erro ao salvar configuração do WhatsApp');
    }
  };

  const testWhatsApp = async () => {
    const testNumber = prompt('Digite o número WhatsApp para teste (ex: 11999999999):');
    if (!testNumber) return;

    try {
      const response = await axios.post(`${API}/test-whatsapp`, {
        whatsapp: testNumber,
        message: 'Teste de mensagem da Arena Jungle! 🏈⚽'
      });
      
      if (response.data.success) {
        alert('Mensagem de teste enviada com sucesso!');
      } else {
        alert(`Erro no teste: ${response.data.message}`);
      }
      
      // Refresh logs
      fetchConfigurations();
    } catch (error) {
      console.error('Erro no teste WhatsApp:', error);
      alert('Erro ao enviar teste');
    }
  };

  const editTemplate = async (template) => {
    const newMessage = prompt('Edite a mensagem:', template.message);
    if (newMessage && newMessage !== template.message) {
      try {
        await axios.put(`${API}/message-templates/${template.id}`, {
          ...template,
          message: newMessage
        });
        alert('Template atualizado com sucesso!');
        fetchConfigurations(); // Refresh data
      } catch (error) {
        console.error('Erro ao atualizar template:', error);
        alert('Erro ao atualizar template');
      }
    }
  };

  // PIX and Banking functions
  const saveBankConfig = async () => {
    try {
      await axios.put(`${API}/config/bank`, bankConfig);
      alert('Configuração bancária e PIX salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração bancária:', error);
      alert('Erro ao salvar configuração bancária');
    }
  };

  const validatePixKey = async () => {
    if (!bankConfig.pix_key || !bankConfig.pix_key_type) {
      alert('Informe a chave PIX e o tipo antes de validar.');
      return;
    }

    try {
      const response = await axios.post(`${API}/pix/validate-key`, {
        pix_key: bankConfig.pix_key,
        key_type: bankConfig.pix_key_type
      });
      
      if (response.data.valid) {
        alert('✅ Chave PIX válida!');
      } else {
        alert(`❌ ${response.data.message}`);
      }
    } catch (error) {
      console.error('Erro ao validar chave PIX:', error);
      alert('Erro ao validar chave PIX');
    }
  };

  const generateTestPix = async () => {
    const customerName = prompt('Nome do cliente para teste:');
    const amount = prompt('Valor do PIX (ex: 125.00):');
    
    if (!customerName || !amount) return;

    try {
      const response = await axios.post(`${API}/pix/generate`, {
        customer_name: customerName,
        customer_document: '123.456.789-00',
        amount: parseFloat(amount),
        booking_id: 'test_booking'
      });
      
      alert('PIX de teste gerado com sucesso!');
      fetchConfigurations(); // Refresh data
    } catch (error) {
      console.error('Erro ao gerar PIX teste:', error);
      alert(error.response?.data?.detail || 'Erro ao gerar PIX teste');
    }
  };

  const confirmPixPayment = async (paymentId) => {
    const transactionId = prompt('ID da transação PIX (opcional):') || 'manual_confirmation';
    
    try {
      await axios.put(`${API}/pix/${paymentId}/confirm`, {
        transaction_id: transactionId
      });
      
      alert('Pagamento PIX confirmado com sucesso!');
      fetchConfigurations(); // Refresh data
    } catch (error) {
      console.error('Erro ao confirmar pagamento PIX:', error);
      alert('Erro ao confirmar pagamento PIX');
    }
  };

  const getPixKeyPlaceholder = (keyType) => {
    switch (keyType) {
      case 'cpf': return '000.000.000-00';
      case 'cnpj': return '00.000.000/0000-00';
      case 'email': return 'exemplo@email.com';
      case 'phone': return '+5511999999999';
      case 'random': return '123e4567-e89b-12d3-a456-426614174000';
      default: return '';
    }
  };

  // Bank Integration functions
  const testBankIntegration = async () => {
    try {
      const response = await axios.post(`${API}/bank/test-integration`);
      
      if (response.data.status === 'success') {
        const results = response.data.results;
        let message = `✅ Teste de Integração:\n\n`;
        message += `Provedor: ${results?.provider || 'Manual'}\n`;
        message += `Banco: ${results?.bank_name || 'N/A'}\n`;
        message += `API Configurada: ${results?.api_configured ? 'Sim' : 'Não'}\n`;
        message += `Webhook Configurado: ${results?.webhook_configured ? 'Sim' : 'Não'}\n`;
        message += `Status: ${results?.status || 'N/A'}`;
        
        alert(message);
      } else {
        alert(`❌ Erro no teste: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Erro ao testar integração:', error);
      alert('Erro ao testar integração bancária');
    }
  };

  const simulateBankPix = async () => {
    const amount = prompt('Valor do PIX para simular (ex: 125.00):');
    if (!amount) return;

    const payerName = prompt('Nome do pagador:') || 'Cliente Simulado';

    try {
      const response = await axios.post(`${API}/bank/simulate-pix`, {
        amount: parseFloat(amount),
        payer_name: payerName,
        payer_document: '123.456.789-00'
      });
      
      alert('🏦 Simulação de PIX bancário executada com sucesso!\n\nO sistema processou o webhook como se fosse uma notificação real do banco.');
      
      // Refresh data to show new webhook and payment status
      fetchConfigurations();
    } catch (error) {
      console.error('Erro na simulação:', error);
      alert('Erro ao simular PIX bancário');
    }
  };

  const checkPixStatusWithBank = async (paymentId) => {
    try {
      const response = await axios.post(`${API}/bank/check-pix-status`, {
        payment_id: paymentId
      });
      
      if (response.data.status === 'success') {
        alert(`🏦 Status no banco: ${response.data.bank_status}\n\nConsultado em: ${new Date(response.data.checked_at).toLocaleString('pt-BR')}`);
        
        // Refresh data if payment was confirmed
        if (response.data.bank_status === 'paid') {
          fetchConfigurations();
        }
      } else {
        alert(`❌ Erro: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Erro ao consultar banco:', error);
      alert('Erro ao consultar status no banco');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#22c55e';
      case 'pending': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmado';
      case 'pending': return 'Pendente';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const calculateStats = () => {
    const total = bookings.length;
    const confirmed = bookings.filter(b => b.status === 'confirmed').length;
    const pending = bookings.filter(b => b.status === 'pending').length;
    const cancelled = bookings.filter(b => b.status === 'cancelled').length;
    
    const totalRevenue = bookings
      .filter(b => b.status === 'confirmed')
      .reduce((sum, b) => sum + b.price, 0);
    
    const pixRevenue = bookings
      .filter(b => b.status === 'confirmed' && b.payment_method === 'pix')
      .reduce((sum, b) => sum + b.price, 0);
    
    const cardRevenue = bookings
      .filter(b => b.status === 'confirmed' && b.payment_method !== 'pix')
      .reduce((sum, b) => sum + b.price, 0);

    return {
      total,
      confirmed,
      pending,
      cancelled,
      totalRevenue,
      pixRevenue,
      cardRevenue
    };
  };

  const stats = calculateStats();

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', color: 'white' }}>
      {/* Header */}
      <header className="app-header">
        <div className="logo-container">
          <img 
            src="/arena-jungle-logo.png" 
            alt="Arena Jungle" 
            data-testid="admin-logo"
          />
        </div>
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <h1 style={{ color: '#22c55e', fontSize: '24px', fontWeight: '600' }}>
            Painel Administrativo
          </h1>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* Navigation Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '30px',
          borderBottom: '2px solid #22c55e',
          paddingBottom: '10px',
          flexWrap: 'wrap'
        }}>
          <button
            className={`btn ${activeTab === 'dashboard' ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setActiveTab('dashboard')}
            data-testid="dashboard-tab"
          >
            Dashboard
          </button>
          <button
            className={`btn ${activeTab === 'bookings' ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setActiveTab('bookings')}
            data-testid="bookings-tab"
          >
            Agendamentos
          </button>
          <button
            className={`btn ${activeTab === 'users' ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setActiveTab('users')}
            data-testid="users-tab"
          >
            Funcionários
          </button>
          <button
            className={`btn ${activeTab === 'config' ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setActiveTab('config')}
            data-testid="config-tab"
          >
            Configurações
          </button>
          <button
            className={`btn ${activeTab === 'links' ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setActiveTab('links')}
            data-testid="links-tab"
          >
            Links Pagamento
          </button>
          <button
            className={`btn ${activeTab === 'messages' ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setActiveTab('messages')}
            data-testid="messages-tab"
          >
            WhatsApp
          </button>
          <button
            className={`btn ${activeTab === 'pix' ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setActiveTab('pix')}
            data-testid="pix-tab"
          >
            PIX/Bancário
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            <h2 style={{ color: '#22c55e', marginBottom: '20px' }}>Resumo Geral</h2>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '10px' }}>Total de Agendamentos</h3>
                <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{stats.total}</div>
              </div>
              
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '10px' }}>Confirmados</h3>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#22c55e' }}>{stats.confirmed}</div>
              </div>
              
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #f59e0b'
              }}>
                <h3 style={{ color: '#f59e0b', marginBottom: '10px' }}>Pendentes</h3>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>{stats.pending}</div>
              </div>
              
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #ef4444'
              }}>
                <h3 style={{ color: '#ef4444', marginBottom: '10px' }}>Cancelados</h3>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ef4444' }}>{stats.cancelled}</div>
              </div>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '20px'
            }}>
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '10px' }}>Receita Total</h3>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  R$ {stats.totalRevenue.toFixed(2)}
                </div>
              </div>
              
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '10px' }}>Receita PIX</h3>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  R$ {stats.pixRevenue.toFixed(2)}
                </div>
              </div>
              
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '10px' }}>Receita Cartão</h3>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  R$ {stats.cardRevenue.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#22c55e' }}>Agendamentos</h2>
              <button
                className="btn btn-primary"
                onClick={fetchBookings}
                disabled={loading}
                data-testid="refresh-bookings-button"
              >
                {loading ? 'Carregando...' : 'Atualizar'}
              </button>
            </div>

            {loading && (
              <div className="loading" data-testid="bookings-loading">
                Carregando agendamentos...
              </div>
            )}

            {!loading && bookings.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                Nenhum agendamento encontrado.
              </div>
            )}

            {!loading && bookings.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#111',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <thead>
                    <tr style={{ background: '#22c55e' }}>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Cliente</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>WhatsApp</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Data</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Horário</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Pagamento</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Valor</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map(booking => (
                        <tr key={booking.id} style={{ borderBottom: '1px solid #333' }}>
                          <td style={{ padding: '12px' }}>{booking.customer_name}</td>
                          <td style={{ padding: '12px' }}>{booking.customer_whatsapp}</td>
                          <td style={{ padding: '12px' }}>{formatDate(booking.date)}</td>
                          <td style={{ padding: '12px' }}>{booking.time_slot}</td>
                          <td style={{ padding: '12px' }}>
                            {booking.payment_method === 'pix' ? 'PIX' : 
                             booking.payment_method === 'credit_card' ? 'Crédito' : 'Débito'}
                          </td>
                          <td style={{ padding: '12px' }}>R$ {booking.price.toFixed(2)}</td>
                          <td style={{ padding: '12px' }}>
                            <span style={{ 
                              color: getStatusColor(booking.status),
                              fontWeight: 'bold'
                            }}>
                              {getStatusText(booking.status)}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {booking.status === 'pending' && (
                                <button
                                  style={{
                                    padding: '6px 12px',
                                    background: '#22c55e',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                  onClick={() => confirmPayment(booking.id)}
                                  data-testid={`confirm-payment-${booking.id}`}
                                >
                                  Confirmar
                                </button>
                              )}
                              {booking.status !== 'cancelled' && (
                                <button
                                  style={{
                                    padding: '6px 12px',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                  onClick={() => cancelBooking(booking.id)}
                                  data-testid={`cancel-booking-${booking.id}`}
                                >
                                  Cancelar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#22c55e' }}>Gerenciar Funcionários</h2>
              <button
                className="btn btn-success"
                onClick={() => setShowUserForm(true)}
                data-testid="add-user-button"
              >
                Adicionar Funcionário
              </button>
            </div>

            {users.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                Nenhum funcionário cadastrado.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#111',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <thead>
                    <tr style={{ background: '#22c55e' }}>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Nome</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Usuário</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Função</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} style={{ borderBottom: '1px solid #333' }}>
                        <td style={{ padding: '12px' }}>{user.name}</td>
                        <td style={{ padding: '12px' }}>{user.username}</td>
                        <td style={{ padding: '12px' }}>{user.email}</td>
                        <td style={{ padding: '12px' }}>
                          {user.role === 'admin' ? 'Administrador' : 
                           user.role === 'manager' ? 'Gestor' : 'Atendente'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ color: user.active ? '#22c55e' : '#ef4444' }}>
                            {user.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <button
                            style={{
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            onClick={() => deleteUser(user.id)}
                            data-testid={`delete-user-${user.id}`}
                          >
                            Desativar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <div>
            <h2 style={{ color: '#22c55e', marginBottom: '30px' }}>Configurações do Sistema</h2>
            
            <div style={{ display: 'grid', gap: '30px' }}>
              {/* Price Configuration */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Configuração de Preços</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>17h e 18h (PIX)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={priceConfig.time_slots_17_18 || 125}
                      onChange={(e) => setPriceConfig({...priceConfig, time_slots_17_18: parseFloat(e.target.value)})}
                      data-testid="price-17-18"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>19h e 22h (PIX)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={priceConfig.time_slots_19_22 || 140}
                      onChange={(e) => setPriceConfig({...priceConfig, time_slots_19_22: parseFloat(e.target.value)})}
                      data-testid="price-19-22"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>20h e 21h (PIX)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={priceConfig.time_slots_20_21 || 150}
                      onChange={(e) => setPriceConfig({...priceConfig, time_slots_20_21: parseFloat(e.target.value)})}
                      data-testid="price-20-21"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Cartão (todos os horários)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={priceConfig.card_price || 160}
                      onChange={(e) => setPriceConfig({...priceConfig, card_price: parseFloat(e.target.value)})}
                      data-testid="price-card"
                    />
                  </div>
                </div>
                <button
                  className="btn btn-success"
                  onClick={savePriceConfig}
                  style={{ marginTop: '15px' }}
                  data-testid="save-prices"
                >
                  Salvar Preços
                </button>
              </div>

              {/* Business Rules */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Regras de Negócio</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Limite para reagendamento (horas)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={businessRules.reschedule_hours_limit || 3}
                      onChange={(e) => setBusinessRules({...businessRules, reschedule_hours_limit: parseInt(e.target.value)})}
                      data-testid="reschedule-limit"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Máximo de dias para agendamento antecipado</label>
                    <input
                      type="number"
                      className="form-input"
                      value={businessRules.max_advance_booking_days || 30}
                      onChange={(e) => setBusinessRules({...businessRules, max_advance_booking_days: parseInt(e.target.value)})}
                      data-testid="max-advance-days"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                    <input
                      type="checkbox"
                      checked={businessRules.allow_same_day_booking}
                      onChange={(e) => setBusinessRules({...businessRules, allow_same_day_booking: e.target.checked})}
                      data-testid="allow-same-day"
                    />
                    <label>Permitir agendamento no mesmo dia</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                    <input
                      type="checkbox"
                      checked={businessRules.auto_confirm_pix}
                      onChange={(e) => setBusinessRules({...businessRules, auto_confirm_pix: e.target.checked})}
                      data-testid="auto-confirm-pix"
                    />
                    <label>Confirmar automaticamente pagamentos PIX</label>
                  </div>
                </div>
                <button
                  className="btn btn-success"
                  onClick={saveBusinessRules}
                  style={{ marginTop: '15px' }}
                  data-testid="save-rules"
                >
                  Salvar Regras
                </button>
              </div>

              {/* Company Info */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Informações da Empresa</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Nome da Empresa</label>
                    <input
                      type="text"
                      className="form-input"
                      value={companyInfo.name || 'Arena Jungle'}
                      onChange={(e) => setCompanyInfo({...companyInfo, name: e.target.value})}
                      data-testid="company-name"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Endereço</label>
                    <input
                      type="text"
                      className="form-input"
                      value={companyInfo.address || ''}
                      onChange={(e) => setCompanyInfo({...companyInfo, address: e.target.value})}
                      data-testid="company-address"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Telefone</label>
                    <input
                      type="text"
                      className="form-input"
                      value={companyInfo.phone || ''}
                      onChange={(e) => setCompanyInfo({...companyInfo, phone: e.target.value})}
                      data-testid="company-phone"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={companyInfo.email || ''}
                      onChange={(e) => setCompanyInfo({...companyInfo, email: e.target.value})}
                      data-testid="company-email"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>WhatsApp</label>
                    <input
                      type="text"
                      className="form-input"
                      value={companyInfo.whatsapp || ''}
                      onChange={(e) => setCompanyInfo({...companyInfo, whatsapp: e.target.value})}
                      data-testid="company-whatsapp"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Horário de Funcionamento</label>
                    <input
                      type="text"
                      className="form-input"
                      value={companyInfo.opening_hours || '17:00 - 23:00'}
                      onChange={(e) => setCompanyInfo({...companyInfo, opening_hours: e.target.value})}
                      data-testid="opening-hours"
                    />
                  </div>
                </div>
                <button
                  className="btn btn-success"
                  onClick={saveCompanyInfo}
                  style={{ marginTop: '15px' }}
                  data-testid="save-company"
                >
                  Salvar Informações
                </button>
              </div>

              {/* WhatsApp Configuration */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Configuração WhatsApp</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>API Key WhatsApp</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Sua chave da API WhatsApp"
                      value={whatsappConfig.api_key || ''}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, api_key: e.target.value})}
                      data-testid="whatsapp-api-key"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Número WhatsApp Business</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="(11) 99999-9999"
                      value={whatsappConfig.phone_number || ''}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, phone_number: e.target.value})}
                      data-testid="whatsapp-phone"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Lembrete 1 (horas antes)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={whatsappConfig.reminder_1_hours || 2}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, reminder_1_hours: parseInt(e.target.value)})}
                      data-testid="reminder-1-hours"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Lembrete 2 (horas antes)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={whatsappConfig.reminder_2_hours || 1}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, reminder_2_hours: parseInt(e.target.value)})}
                      data-testid="reminder-2-hours"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                    <input
                      type="checkbox"
                      checked={whatsappConfig.enabled}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, enabled: e.target.checked})}
                      data-testid="whatsapp-enabled"
                    />
                    <label>Ativar envio de mensagens WhatsApp</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                    <input
                      type="checkbox"
                      checked={whatsappConfig.send_confirmation}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, send_confirmation: e.target.checked})}
                      data-testid="send-confirmation"
                    />
                    <label>Enviar mensagem de confirmação</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                    <input
                      type="checkbox"
                      checked={whatsappConfig.send_reminders}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, send_reminders: e.target.checked})}
                      data-testid="send-reminders"
                    />
                    <label>Enviar lembretes automáticos</label>
                  </div>
                </div>
                <button
                  className="btn btn-success"
                  onClick={saveWhatsAppConfig}
                  style={{ marginTop: '15px' }}
                  data-testid="save-whatsapp"
                >
                  Salvar Configurações WhatsApp
                </button>
              </div>

              {/* PIX and Bank Configuration */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Configuração PIX e Bancária</h3>
                
                {/* PIX Configuration */}
                <div style={{ marginBottom: '30px' }}>
                  <h4 style={{ color: '#22c55e', marginBottom: '15px', fontSize: '18px' }}>Dados PIX</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Tipo de Chave PIX</label>
                      <select
                        className="form-input"
                        value={bankConfig.pix_key_type || 'cpf'}
                        onChange={(e) => setBankConfig({...bankConfig, pix_key_type: e.target.value})}
                        data-testid="pix-key-type"
                      >
                        <option value="cpf">CPF</option>
                        <option value="cnpj">CNPJ</option>
                        <option value="email">Email</option>
                        <option value="phone">Telefone</option>
                        <option value="random">Chave Aleatória</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Chave PIX</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder={getPixKeyPlaceholder(bankConfig.pix_key_type)}
                        value={bankConfig.pix_key || ''}
                        onChange={(e) => setBankConfig({...bankConfig, pix_key: e.target.value})}
                        data-testid="pix-key"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Tempo de Expiração PIX (minutos)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={bankConfig.pix_expiration_minutes || 30}
                        onChange={(e) => setBankConfig({...bankConfig, pix_expiration_minutes: parseInt(e.target.value)})}
                        data-testid="pix-expiration"
                      />
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div style={{ marginBottom: '30px' }}>
                  <h4 style={{ color: '#22c55e', marginBottom: '15px', fontSize: '18px' }}>Dados Bancários</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Nome do Banco</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Ex: Banco do Brasil"
                        value={bankConfig.bank_name || ''}
                        onChange={(e) => setBankConfig({...bankConfig, bank_name: e.target.value})}
                        data-testid="bank-name"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Código do Banco</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Ex: 001"
                        value={bankConfig.bank_code || ''}
                        onChange={(e) => setBankConfig({...bankConfig, bank_code: e.target.value})}
                        data-testid="bank-code"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Agência</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Ex: 1234-5"
                        value={bankConfig.agency || ''}
                        onChange={(e) => setBankConfig({...bankConfig, agency: e.target.value})}
                        data-testid="bank-agency"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Conta</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Ex: 12345-6"
                        value={bankConfig.account || ''}
                        onChange={(e) => setBankConfig({...bankConfig, account: e.target.value})}
                        data-testid="bank-account"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Tipo da Conta</label>
                      <select
                        className="form-input"
                        value={bankConfig.account_type || 'corrente'}
                        onChange={(e) => setBankConfig({...bankConfig, account_type: e.target.value})}
                        data-testid="account-type"
                      >
                        <option value="corrente">Conta Corrente</option>
                        <option value="poupanca">Poupança</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Account Holder */}
                <div style={{ marginBottom: '30px' }}>
                  <h4 style={{ color: '#22c55e', marginBottom: '15px', fontSize: '18px' }}>Titular da Conta</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Nome do Titular</label>
                      <input
                        type="text"
                        className="form-input"
                        value={bankConfig.holder_name || ''}
                        onChange={(e) => setBankConfig({...bankConfig, holder_name: e.target.value})}
                        data-testid="holder-name"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>Tipo de Documento</label>
                      <select
                        className="form-input"
                        value={bankConfig.holder_document_type || 'cpf'}
                        onChange={(e) => setBankConfig({...bankConfig, holder_document_type: e.target.value})}
                        data-testid="holder-doc-type"
                      >
                        <option value="cpf">CPF</option>
                        <option value="cnpj">CNPJ</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>
                        {bankConfig.holder_document_type === 'cnpj' ? 'CNPJ' : 'CPF'}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder={bankConfig.holder_document_type === 'cnpj' ? '00.000.000/0000-00' : '000.000.000-00'}
                        value={bankConfig.holder_document || ''}
                        onChange={(e) => setBankConfig({...bankConfig, holder_document: e.target.value})}
                        data-testid="holder-document"
                      />
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#22c55e', marginBottom: '15px', fontSize: '18px' }}>Configurações</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={bankConfig.active}
                        onChange={(e) => setBankConfig({...bankConfig, active: e.target.checked})}
                        data-testid="bank-active"
                      />
                      <label>Ativar configuração bancária</label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={bankConfig.generate_qr_code}
                        onChange={(e) => setBankConfig({...bankConfig, generate_qr_code: e.target.checked})}
                        data-testid="generate-qr"
                      />
                      <label>Gerar QR Code automático</label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={bankConfig.auto_confirm_pix}
                        onChange={(e) => setBankConfig({...bankConfig, auto_confirm_pix: e.target.checked})}
                        data-testid="auto-confirm-pix-bank"
                      />
                      <label>Confirmar PIX automaticamente</label>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px' }}>
                  <button
                    className="btn btn-success"
                    onClick={saveBankConfig}
                    data-testid="save-bank"
                  >
                    Salvar Configuração Bancária
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={validatePixKey}
                    data-testid="validate-pix"
                  >
                    Validar Chave PIX
                  </button>
                </div>
              </div>

              {/* Bank API Integration */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Integração Bancária Automática</h3>
                
                <div style={{ marginBottom: '20px', padding: '15px', background: '#1a1a1a', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                  <h4 style={{ color: '#f59e0b', marginBottom: '10px' }}>⚠️ Confirmação Automática de PIX</h4>
                  <p style={{ color: '#e5e7eb', fontSize: '14px', lineHeight: '1.5' }}>
                    Para receber confirmações automáticas do banco quando um PIX é recebido, configure a integração abaixo. 
                    O sistema irá automaticamente confirmar pagamentos e notificar os clientes via WhatsApp.
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Provedor de Pagamento</label>
                    <select
                      className="form-input"
                      value={bankConfig.payment_provider || 'manual'}
                      onChange={(e) => setBankConfig({...bankConfig, payment_provider: e.target.value})}
                      data-testid="payment-provider"
                    >
                      <option value="manual">Manual (sem integração)</option>
                      <option value="banco_do_brasil">Banco do Brasil API</option>
                      <option value="itau">Itaú API</option>
                      <option value="mercadopago">Mercado Pago</option>
                      <option value="pagseguro">PagSeguro</option>
                      <option value="cielo">Cielo</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>API Key / Client ID</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Chave da API do banco/provedor"
                      value={bankConfig.bank_client_id || ''}
                      onChange={(e) => setBankConfig({...bankConfig, bank_client_id: e.target.value})}
                      data-testid="bank-client-id"
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>API Secret</label>
                    <input
                      type="password"
                      className="form-input"
                      placeholder="Chave secreta da API"
                      value={bankConfig.bank_client_secret || ''}
                      onChange={(e) => setBankConfig({...bankConfig, bank_client_secret: e.target.value})}
                      data-testid="bank-client-secret"
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Endpoint da API</label>
                    <input
                      type="url"
                      className="form-input"
                      placeholder="https://api.banco.com.br/pix"
                      value={bankConfig.bank_api_endpoint || ''}
                      onChange={(e) => setBankConfig({...bankConfig, bank_api_endpoint: e.target.value})}
                      data-testid="bank-api-endpoint"
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>URL do Webhook</label>
                    <input
                      type="url"
                      className="form-input"
                      placeholder="https://seu-site.com/webhook/pix"
                      value={bankConfig.webhook_url || 'https://book-jungle.preview.emergentagent.com/api/webhook/pix'}
                      onChange={(e) => setBankConfig({...bankConfig, webhook_url: e.target.value})}
                      data-testid="webhook-url"
                    />
                  </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <input
                      type="checkbox"
                      checked={bankConfig.bank_api_enabled}
                      onChange={(e) => setBankConfig({...bankConfig, bank_api_enabled: e.target.checked})}
                      data-testid="bank-api-enabled"
                    />
                    <label>Habilitar integração bancária automática</label>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={bankConfig.open_banking_enabled}
                      onChange={(e) => setBankConfig({...bankConfig, open_banking_enabled: e.target.checked})}
                      data-testid="open-banking-enabled"
                    />
                    <label>Usar Open Banking (requer certificados)</label>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                  <button
                    className="btn btn-success"
                    onClick={saveBankConfig}
                    data-testid="save-integration"
                  >
                    Salvar Integração
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={testBankIntegration}
                    data-testid="test-integration"
                  >
                    Testar Conexão
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={simulateBankPix}
                    data-testid="simulate-bank-pix"
                  >
                    Simular PIX do Banco
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Links Tab */}
        {activeTab === 'links' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#22c55e' }}>Links de Pagamento</h2>
              <button
                className="btn btn-success"
                onClick={() => setShowLinkForm(true)}
                data-testid="add-link-button"
              >
                Gerar Link
              </button>
            </div>

            {paymentLinks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                Nenhum link de pagamento gerado.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#111',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <thead>
                    <tr style={{ background: '#22c55e' }}>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Cliente</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>WhatsApp</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Valor</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Descrição</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Expira</th>
                      <th style={{ padding: '15px', textAlign: 'left' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentLinks.map(link => (
                      <tr key={link.id} style={{ borderBottom: '1px solid #333' }}>
                        <td style={{ padding: '12px' }}>{link.customer_name}</td>
                        <td style={{ padding: '12px' }}>{link.customer_whatsapp}</td>
                        <td style={{ padding: '12px' }}>R$ {link.amount.toFixed(2)}</td>
                        <td style={{ padding: '12px' }}>{link.description}</td>
                        <td style={{ padding: '12px' }}>
                          {new Date(link.expires_at).toLocaleString('pt-BR')}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ color: link.used ? '#ef4444' : '#22c55e' }}>
                            {link.used ? 'Usado' : 'Ativo'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* WhatsApp Messages Tab */}
        {activeTab === 'messages' && (
          <div>
            <h2 style={{ color: '#22c55e', marginBottom: '30px' }}>Gestão de Mensagens WhatsApp</h2>
            
            <div style={{ display: 'grid', gap: '30px' }}>
              {/* Message Templates */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ color: '#22c55e' }}>Templates de Mensagens</h3>
                  <button
                    className="btn btn-success"
                    onClick={testWhatsApp}
                    data-testid="test-whatsapp"
                  >
                    Testar WhatsApp
                  </button>
                </div>

                {messageTemplates.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                    Carregando templates...
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '20px' }}>
                    {messageTemplates.map(template => (
                      <div key={template.id} style={{
                        background: '#1a1a1a',
                        padding: '15px',
                        borderRadius: '8px',
                        border: template.active ? '1px solid #22c55e' : '1px solid #666'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h4 style={{ color: '#22c55e' }}>{template.name}</h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ color: template.active ? '#22c55e' : '#666' }}>
                              {template.active ? 'Ativo' : 'Inativo'}
                            </span>
                            <button
                              style={{
                                padding: '4px 8px',
                                background: '#374151',
                                color: 'white',
                                border: '1px solid #22c55e',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              onClick={() => editTemplate(template)}
                              data-testid={`edit-template-${template.type}`}
                            >
                              Editar
                            </button>
                          </div>
                        </div>
                        <div style={{
                          background: '#333',
                          padding: '10px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          whiteSpace: 'pre-line',
                          maxHeight: '150px',
                          overflow: 'auto'
                        }}>
                          {template.message}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Message Logs */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Histórico de Mensagens</h3>
                
                {messageLogs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    Nenhuma mensagem enviada ainda.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      background: '#1a1a1a',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <thead>
                        <tr style={{ background: '#22c55e' }}>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Data/Hora</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>WhatsApp</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Tipo</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Mensagem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {messageLogs.slice(0, 20).map(log => (
                          <tr key={log.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '10px' }}>
                              {new Date(log.sent_at).toLocaleString('pt-BR')}
                            </td>
                            <td style={{ padding: '10px' }}>{log.customer_whatsapp}</td>
                            <td style={{ padding: '10px' }}>
                              {log.message_type === 'confirmation' ? 'Confirmação' :
                               log.message_type === 'reminder_1' ? 'Lembrete 2h' :
                               log.message_type === 'reminder_2' ? 'Lembrete 1h' :
                               log.message_type === 'cancellation' ? 'Cancelamento' :
                               log.message_type}
                            </td>
                            <td style={{ padding: '10px' }}>
                              <span style={{ 
                                color: log.status === 'sent' ? '#22c55e' : 
                                       log.status === 'failed' ? '#ef4444' : '#f59e0b'
                              }}>
                                {log.status === 'sent' ? 'Enviado' :
                                 log.status === 'failed' ? 'Falhou' : 'Pendente'}
                              </span>
                            </td>
                            <td style={{ padding: '10px', maxWidth: '300px' }}>
                              <div style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {log.message_content.substring(0, 50)}...
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PIX/Banking Tab */}
        {activeTab === 'pix' && (
          <div>
            <h2 style={{ color: '#22c55e', marginBottom: '30px' }}>Gestão PIX e Pagamentos</h2>
            
            <div style={{ display: 'grid', gap: '30px' }}>
              {/* PIX Payments List */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ color: '#22c55e' }}>Pagamentos PIX</h3>
                  <button
                    className="btn btn-success"
                    onClick={generateTestPix}
                    data-testid="generate-test-pix"
                  >
                    Gerar PIX Teste
                  </button>
                </div>

                {pixPayments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    Nenhum pagamento PIX encontrado.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      background: '#1a1a1a',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <thead>
                        <tr style={{ background: '#22c55e' }}>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Cliente</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Valor</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Código PIX</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Criado</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Expira</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pixPayments.slice(0, 20).map(payment => (
                          <tr key={payment.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '10px' }}>{payment.customer_name}</td>
                            <td style={{ padding: '10px' }}>R$ {payment.amount?.toFixed(2)}</td>
                            <td style={{ padding: '10px' }}>
                              <span style={{ 
                                fontFamily: 'monospace', 
                                fontSize: '12px',
                                color: '#22c55e'
                              }}>
                                {payment.pix_code || 'N/A'}
                              </span>
                            </td>
                            <td style={{ padding: '10px' }}>
                              <span style={{ 
                                color: payment.status === 'paid' ? '#22c55e' : 
                                       payment.status === 'expired' ? '#ef4444' : '#f59e0b'
                              }}>
                                {payment.status === 'paid' ? 'Pago' :
                                 payment.status === 'expired' ? 'Expirado' : 
                                 payment.status === 'cancelled' ? 'Cancelado' : 'Pendente'}
                              </span>
                            </td>
                            <td style={{ padding: '10px' }}>
                              {new Date(payment.created_at).toLocaleString('pt-BR')}
                            </td>
                            <td style={{ padding: '10px' }}>
                              {new Date(payment.expires_at).toLocaleString('pt-BR')}
                            </td>
                            <td style={{ padding: '10px' }}>
                              <div style={{ display: 'flex', gap: '5px' }}>
                                {payment.status === 'pending' && (
                                  <>
                                    <button
                                      style={{
                                        padding: '4px 8px',
                                        background: '#22c55e',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                      onClick={() => confirmPixPayment(payment.id)}
                                      data-testid={`confirm-pix-${payment.id}`}
                                    >
                                      Confirmar
                                    </button>
                                    <button
                                      style={{
                                        padding: '4px 8px',
                                        background: '#374151',
                                        color: 'white',
                                        border: '1px solid #22c55e',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                      onClick={() => checkPixStatusWithBank(payment.id)}
                                      data-testid={`check-bank-${payment.id}`}
                                    >
                                      Consultar Banco
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* PIX Configuration Status */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Status da Configuração PIX</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#888' }}>Chave PIX</label>
                    <div style={{ 
                      padding: '10px', 
                      background: '#1a1a1a', 
                      borderRadius: '4px',
                      color: bankConfig.pix_key ? '#22c55e' : '#ef4444'
                    }}>
                      {bankConfig.pix_key || 'Não configurada'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#888' }}>Banco</label>
                    <div style={{ 
                      padding: '10px', 
                      background: '#1a1a1a', 
                      borderRadius: '4px',
                      color: bankConfig.bank_name ? '#22c55e' : '#888'
                    }}>
                      {bankConfig.bank_name || 'Não informado'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#888' }}>Titular</label>
                    <div style={{ 
                      padding: '10px', 
                      background: '#1a1a1a', 
                      borderRadius: '4px',
                      color: bankConfig.holder_name ? '#22c55e' : '#888'
                    }}>
                      {bankConfig.holder_name || 'Não informado'}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#888' }}>Status</label>
                    <div style={{ 
                      padding: '10px', 
                      background: '#1a1a1a', 
                      borderRadius: '4px',
                      color: bankConfig.active ? '#22c55e' : '#ef4444'
                    }}>
                      {bankConfig.active ? 'Ativo' : 'Inativo'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Webhook Events Log */}
              <div style={{
                background: '#111',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #22c55e'
              }}>
                <h3 style={{ color: '#22c55e', marginBottom: '20px' }}>Log de Webhooks Bancários</h3>
                
                {webhookEvents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                    Nenhum webhook recebido ainda.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      background: '#1a1a1a',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <thead>
                        <tr style={{ background: '#22c55e' }}>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Data/Hora</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Tipo</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Valor</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Pagador</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>ID Transação</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Processado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {webhookEvents.slice(0, 10).map(event => (
                          <tr key={event.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '10px' }}>
                              {new Date(event.received_at).toLocaleString('pt-BR')}
                            </td>
                            <td style={{ padding: '10px' }}>
                              <span style={{ 
                                color: event.event_type === 'pix_received' ? '#22c55e' : '#f59e0b',
                                fontWeight: '500'
                              }}>
                                {event.event_type === 'pix_received' ? 'PIX Recebido' :
                                 event.event_type === 'pix_confirmed' ? 'PIX Confirmado' : 
                                 event.event_type}
                              </span>
                            </td>
                            <td style={{ padding: '10px' }}>R$ {event.amount?.toFixed(2)}</td>
                            <td style={{ padding: '10px' }}>{event.payer_name || 'N/A'}</td>
                            <td style={{ padding: '10px' }}>
                              <span style={{ 
                                fontFamily: 'monospace', 
                                fontSize: '12px',
                                color: '#22c55e'
                              }}>
                                {event.transaction_id}
                              </span>
                            </td>
                            <td style={{ padding: '10px' }}>
                              <span style={{ 
                                color: event.processed ? '#22c55e' : '#f59e0b'
                              }}>
                                {event.processed ? '✅ Sim' : '⏳ Não'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quick Navigation */}
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <a
            href="/"
            style={{
              color: '#22c55e',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: '500'
            }}
            data-testid="back-to-booking"
          >
            ← Voltar para Agendamentos
          </a>
        </div>

        {/* User Form Modal */}
        {showUserForm && (
          <UserFormModal 
            onClose={() => setShowUserForm(false)}
            onSuccess={() => {
              setShowUserForm(false);
              fetchUsers();
            }}
          />
        )}

        {/* Payment Link Form Modal */}
        {showLinkForm && (
          <PaymentLinkModal
            onClose={() => setShowLinkForm(false)}
            onSuccess={() => {
              setShowLinkForm(false);
              fetchConfigurations();
            }}
          />
        )}
      </div>
    </div>
  );
};

// User Form Modal Component
const UserFormModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role: 'attendant'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.username || !formData.email || !formData.password) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${BACKEND_URL}/api/users`, formData);
      alert('Funcionário cadastrado com sucesso!');
      onSuccess();
    } catch (error) {
      console.error('Erro ao cadastrar funcionário:', error);
      alert(error.response?.data?.detail || 'Erro ao cadastrar funcionário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-modal" data-testid="user-form-modal">
      <form className="booking-form" onSubmit={handleSubmit} style={{ maxWidth: '500px' }}>
        <h2 className="form-title">Adicionar Funcionário</h2>
        
        <div className="form-group">
          <label className="form-label" htmlFor="name">Nome Completo *</label>
          <input
            type="text"
            id="name"
            className="form-input"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
            data-testid="user-name-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="username">Nome de Usuário *</label>
          <input
            type="text"
            id="username"
            className="form-input"
            value={formData.username}
            onChange={(e) => setFormData({...formData, username: e.target.value})}
            required
            data-testid="user-username-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            className="form-input"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            data-testid="user-email-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="password">Senha *</label>
          <input
            type="password"
            id="password"
            className="form-input"
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            required
            data-testid="user-password-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="role">Função *</label>
          <select
            id="role"
            className="form-input"
            value={formData.role}
            onChange={(e) => setFormData({...formData, role: e.target.value})}
            data-testid="user-role-select"
          >
            <option value="attendant">Atendente</option>
            <option value="manager">Gestor</option>
            <option value="admin">Administrador</option>
          </select>
        </div>

        <div className="button-group">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            data-testid="cancel-user-button"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-success"
            disabled={loading}
            data-testid="save-user-button"
          >
            {loading ? 'Salvando...' : 'Salvar Funcionário'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Payment Link Modal Component
const PaymentLinkModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_whatsapp: '',
    amount: '',
    description: '',
    expires_hours: 24
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.customer_whatsapp || !formData.amount || !formData.description) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${BACKEND_URL}/api/payment-links`, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      alert('Link de pagamento gerado com sucesso!');
      onSuccess();
    } catch (error) {
      console.error('Erro ao gerar link de pagamento:', error);
      alert('Erro ao gerar link de pagamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-modal" data-testid="link-form-modal">
      <form className="booking-form" onSubmit={handleSubmit} style={{ maxWidth: '500px' }}>
        <h2 className="form-title">Gerar Link de Pagamento</h2>
        
        <div className="form-group">
          <label className="form-label" htmlFor="customer_name">Nome do Cliente *</label>
          <input
            type="text"
            id="customer_name"
            className="form-input"
            value={formData.customer_name}
            onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
            required
            data-testid="link-customer-name"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="customer_whatsapp">WhatsApp do Cliente *</label>
          <input
            type="tel"
            id="customer_whatsapp"
            className="form-input"
            placeholder="(11) 99999-9999"
            value={formData.customer_whatsapp}
            onChange={(e) => setFormData({...formData, customer_whatsapp: e.target.value})}
            required
            data-testid="link-customer-whatsapp"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="amount">Valor (R$) *</label>
          <input
            type="number"
            step="0.01"
            id="amount"
            className="form-input"
            value={formData.amount}
            onChange={(e) => setFormData({...formData, amount: e.target.value})}
            required
            data-testid="link-amount"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="description">Descrição *</label>
          <input
            type="text"
            id="description"
            className="form-input"
            placeholder="Ex: Agendamento Arena Jungle - 20h"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            required
            data-testid="link-description"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="expires_hours">Expira em (horas)</label>
          <select
            id="expires_hours"
            className="form-input"
            value={formData.expires_hours}
            onChange={(e) => setFormData({...formData, expires_hours: parseInt(e.target.value)})}
            data-testid="link-expires"
          >
            <option value={1}>1 hora</option>
            <option value={6}>6 horas</option>
            <option value={24}>24 horas</option>
            <option value={48}>48 horas</option>
            <option value={168}>7 dias</option>
          </select>
        </div>

        <div className="button-group">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            data-testid="cancel-link-button"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-success"
            disabled={loading}
            data-testid="save-link-button"
          >
            {loading ? 'Gerando...' : 'Gerar Link'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminDashboard;